import { Fragment, useRef, useState } from "react";
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from "@headlessui/react";
import Swal from "sweetalert2";
import { XMarkIcon } from "@heroicons/react/20/solid";
import Notiflix from "notiflix";
import CustomButton from "../../../components/customButton";
import { addNewUpiId, fetchAllUPI, kycOtpVerification, validateAdhaar } from "../../../services/profile.services";
import { fetchUserDetails, selectUser } from "../../../redux/userDetailsSlice";
import { useDispatch, useSelector } from "react-redux";
import OtpModal from "../kyc/otpModal";
import { validateUserToken } from "../../../services/auth.services";

export default function UpiModal({
  toggled,
  toggleUPImodal,
  upiUpdated,
  setUpiUpdated,
  onClose
}: any) {
  const user = useSelector(selectUser);
  const [open, setOpen] = useState(toggleUPImodal);
  const cancelButtonRef = useRef(null);
  const [upiId, setUpiId] = useState<string>("");
  const [aadhaar, setAadhaar] = useState<string>("");
  const [otp, setOtp] = useState<string>("");
  const [otpError, setOtpError] = useState<string>("");
  const [aadhaarNumber, setAadhaarNumber] = useState<string>("");
  const [tsTransId, setTsTransId] = useState<string>("");
  const [openUpiModal, setOpenUpiModal] = useState(true);
  const [openAadhaarModal, setOpenAadhaarModal] = useState(false);
  const [openOtpModal, setOpenOtpModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string>("");
  const [otpMsg, setotpMsg] = useState<string>();
  const dispatch = useDispatch();

  const validate = () => {
    let upiErrorMess = "";
    if (!upiId) {
      upiErrorMess = "Please enter UPI Id";
    }
    if (upiErrorMess) {
      setError(upiErrorMess);
      return false;
    }
    return true;
  };

  const validateAadhaar = () => {
    let aadhaarErrorMess = "";
    // Remove hyphens before validation
    let rawAadhaar = aadhaar.replace(/-/g, "");

    if (!rawAadhaar) {
      aadhaarErrorMess = "Please enter Aadhaar Number";
    } else if (!/^\d{12}$/.test(rawAadhaar)) {
      aadhaarErrorMess = "Aadhaar Number must be a 12-digit numeric value";
    }

    if (aadhaarErrorMess) {
      setError(aadhaarErrorMess);
      return false;
    }
    return true;
  };

  function handleOTPChange(otp: React.SetStateAction<string>) {
    setOtp(otp);
    setOtpError("");
  }

  const upiIdHandler = (e: any) => {
    setError("");

    let upiIdInput = e.target.value.replace(/[^a-zA-Z0-9.@]/g, "");
    upiIdInput = upiIdInput.replace(/\.+/g, ".");
    let updatedValue = upiIdInput.replace(/\@+/g, "@");
    setUpiId(updatedValue);
  };

  const aadhaarHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(""); // Clear any previous error
    // Remove non-numeric characters
    let aadhaarInput = e.target.value.replace(/\D/g, "");
    // Limit input to 12 digits
    if (aadhaarInput.length > 12) {
      aadhaarInput = aadhaarInput.slice(0, 12);
    }
    // Format as XXXX-XXXX-XXXX
    // Insert a hyphen after every 4 digits
    const formattedAadhaar = aadhaarInput.replace(/(\d{4})(?=\d)/g, "$1-");
    setAadhaar(formattedAadhaar);
  };

  const addNewUPI = async () => {
    if (validate()) {
      if (!isSubmitting) {
        Notiflix.Loading.custom({ svgSize: '180px', customSvgCode: '<object type="image/svg+xml" data="/svg/pageloader.svg">svg-animation</object>' });
        setIsSubmitting(true);
        try {
          const response = await addNewUpiId({ value: upiId, isDefault: 1 });
          Notiflix.Loading.remove();
          if (response.status) {
            await fetchAllUPI();
            Notiflix.Loading.remove();
            setUpiId("");
            Swal.fire({
              html: `<img src="/lottie/Successfully Done.gif" class="swal2-image-custom" alt="Successfully Done">`,
              title: "Successfully Done",
              // titleText: `${response?.message}`,
              titleText: `Your UPI has been added successfully `,
              customClass: {
                popup: "custom-popup",
                confirmButton: "custom-ok-success-btn",
              },
              timer: 2500,
            });
            setUpiUpdated(Date.now());
            onClose();
            setOpenUpiModal(false);
          } else {
            // **Check if response message contains "no match"**
            if (response.message.toLowerCase().includes("not match")) {
              if (user.data.kyc.aadhaarNumber !== null && user.data.kyc.aadhaarNumber !== "") {
                setError(response.message);
                setOpenUpiModal(false); // Close UPI modal
                setOpenAadhaarModal(true);
              } else {
                setError('The name on your upi id does not match your PAN and Aadhar records. Please update your upi details or contact our support team for assistance.');
              }
            } else {
              setError(response.message);
              return;
            }
          }
        } catch (error) {
          Notiflix.Loading.remove();
          setError("Failed to add UPI, please try again later.");
        } finally {
          Notiflix.Loading.remove();
          setIsSubmitting(false);
        };
      }
    }
  };

  const verifyAadhaar = async () => {
    if (validateAadhaar()) {
      if (!isSubmitting) {
        Notiflix.Loading.custom({ svgSize: '180px', customSvgCode: '<object type="image/svg+xml" data="/svg/pageloader.svg">svg-animation</object>' });
        setIsSubmitting(true);
        try {
          const response = await validateAdhaar(aadhaar);
          Notiflix.Loading.remove();
          if (response.status) {
            setotpMsg(response.message);
            setAadhaarNumber(response?.data?.aadharNumber); // Fix: Access nested data
            setTsTransId(response?.data?.tsTransId); // Fix: Access nested data
            setOpenAadhaarModal(false);
            setOpenOtpModal(true);
          } else {
            setError(response.message);
          }
        } catch (error) {
          Notiflix.Loading.remove();
          setError("Failed to validate Aadhaar, please try again.");
        } finally {
          Notiflix.Loading.remove();
          setIsSubmitting(false);
        }
      }
    }
  };

  const handleOTPVerify = async () => {
    if (otp.length !== 6) {
      setOtpError("Please enter a valid 6-digit OTP.");
      return;
    }
    setIsSubmitting(true);
    try {
      const data = {
        aadhaarNumber,
        tsTransId,
        otp,
      };
      const response = await kycOtpVerification(data);

      if (!response || typeof response !== "object") {
        throw new Error("Invalid response format from server");
      }

      if (response.status) {
        Swal.fire({
          icon: "success",
          title: "Verification Successful",
          text: response.message || "OTP verified successfully",
          timer: 1500,
          customClass: {
            popup: "custom-popup",
            confirmButton: "custom-ok-success-btn",
          },
        });
        setOpenOtpModal(false);
        setTimeout(async () => {
          if (!isSubmitting) {
            Notiflix.Loading.custom({ svgSize: '180px', customSvgCode: '<object type="image/svg+xml" data="/svg/pageloader.svg">svg-animation</object>' });
            setIsSubmitting(true);
            try {
              const response = await addNewUpiId({ value: upiId, isDefault: 1 });
              Notiflix.Loading.remove();
              if (response.status) {
                await fetchAllUPI();
                Notiflix.Loading.remove();
                setUpiId("");
                Swal.fire({
                  html: `<img src="/lottie/Successfully Done.gif" class="swal2-image-custom" alt="Successfully Done">`,
                  title: "Successfully Done",
                  // titleText: `${response?.message}`,
                  titleText: `Your UPI has been added successfully `,
                  customClass: {
                    popup: "custom-popup",
                    confirmButton: "custom-ok-success-btn",
                  },
                  timer: 2500,
                });
                // Fetch user data directly
                await validateUserToken();
                // Fetch updated user details
                await dispatch(fetchUserDetails() as any);
                setUpiUpdated(Date.now());
                onClose();
                setOpenUpiModal(false);
              } else {
                // **Check if response message contains "no match"**
                if (response.message.toLowerCase().includes("not match")) {
                  if (user.data.kyc.aadhaarNumber !== null && user.data.kyc.aadhaarNumber !== "") {
                    setError(response.message);
                    setOpenUpiModal(false); // Close UPI modal
                    setOpenAadhaarModal(true);
                  } else {
                    setError('The name on your upi id does not match your PAN and Aadhar records. Please update your upi details or contact our support team for assistance.');
                  }
                } else {
                  setError(response.message);
                  return;
                }
              }
              Notiflix.Loading.remove();
              setError("Failed to add UPI, please try again later.");
            } finally {
              Notiflix.Loading.remove();
              setIsSubmitting(false);
            };
          }
        }, 1500);
        setOtp("");
      } else {
        // Display Swal alert for failed verification, styled like the reference code
        Swal.fire({
          html: `<img src="/lottie/oops.gif" class="swal2-image-customs" alt="Error"><br>`,
          title: "Oops...",
          titleText: response.message || "OTP verification failed",
          customClass: {
            popup: "custom-popup",
            confirmButton: "custom-ok-error-btn",
          },
        });
      }
    } catch (error: any) {
      console.error("Unexpected error in handleOTPVerify:", error);
      Swal.fire({
        icon: "error",
        title: "Verification Failed",
        text: "An unexpected error occurred. Please try again later.",
        customClass: {
          popup: "custom-popup",
          confirmButton: "custom-ok-error-btn",
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Transition show={open} as={Fragment}>
        {openUpiModal && (
          <Dialog
            as="div"
            className="z-[110] fixed inset-0 overflow-y-auto"
            initialFocus={cancelButtonRef}
            onClose={() => {
              return false;
            }}
            data-keyboard="false"
            data-backdrop="static"
          >
            < TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            </ TransitionChild>
            <div className="fixed inset-0 z-10 w-screen overflow-y-auto ">
              <div className="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
                < TransitionChild
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                  enterTo="opacity-100 translate-y-0 sm:scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                  leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                >
                  <DialogPanel className="relative transform overflow-hidden rounded-lg text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                    <div className="bg-theme px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                      <div className="">
                        <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                          <DialogTitle
                            as="h3"
                            className="text-xl sm:text-3xl font-semibold text-center leading-6 text-white mb-3"
                          >
                            Add UPI ID
                          </DialogTitle>
                          <DialogTitle
                            as="h2"
                            className="text-base font-semibold leading-6 text-white"
                          >
                            Enter UPI ID
                          </DialogTitle>
                          <div className="mt-2">
                            <input
                              type="text"
                              className="block w-full placeholder:text-gray-500 text-white rounded bg-theme px-3 py-2 focus:outline-none  border-1 focus:ring-0 focus:border-b"
                              placeholder="Enter UPI ID"
                              value={upiId}
                              onChange={upiIdHandler}
                            />
                          </div>
                          {error && <div className="text-red-600 text-center mt-2">{error}</div>}
                        </div>
                      </div>
                    </div>
                    <div className="bg-theme px-4 py-3 pb-6 flex  justify-center sm:px-6">
                      <CustomButton
                        btnType="button"
                        title="VERIFY AND ADD"
                        loading={isSubmitting}
                        isDisabled={isSubmitting}
                        containerStyles="mt-3 inline-flex w-full justify-center rounded-md bg-themeBlue px-3 py-2 text-sm font-semibold  shadow-sm  sm:mt-0 sm:w-auto"
                        handleClick={addNewUPI}
                      />
                      <button
                        type="submit"
                        className="mt-3 absolute top-5 bg-transparent right-5  justify-center rounded-full border-1 text-white p-2 text-sm font-semibold shadow-sm  sm:mt-0 sm:w-auto"
                        onClick={() => {
                          if (onClose) onClose();
                          setOpen(false);
                        }}
                        ref={cancelButtonRef}
                      >
                        <XMarkIcon className="h-4" />
                      </button>
                    </div>
                  </DialogPanel>
                </ TransitionChild>
              </div>
            </div>
          </Dialog>
        )}
        {openAadhaarModal && (
          <Dialog
            as="div"
            className="z-[110] fixed inset-0 overflow-y-auto"
            initialFocus={cancelButtonRef}
            onClose={() => {
              return false;
            }}
            data-keyboard="false"
            data-backdrop="static"
          >
            < TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            </ TransitionChild>

            <div className="fixed inset-0 z-10 w-screen overflow-y-auto ">
              <div className="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
                < TransitionChild
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                  enterTo="opacity-100 translate-y-0 sm:scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                  leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                >
                  <DialogPanel className="relative transform overflow-hidden rounded-lg text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                    <div className="bg-theme px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                      <div className="">
                        <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                          <DialogTitle
                            as="h3"
                            className="text-xl sm:text-3xl font-semibold text-center leading-6 text-white mb-3"
                          >
                            Add Aadhaar
                          </DialogTitle>
                          <DialogTitle
                            as="h2"
                            className="text-base font-semibold leading-6 text-white"
                          >
                            Enter Aadhaar Number
                          </DialogTitle>
                          <div className="mt-2">
                            <input
                              type="text"
                              className="block w-full placeholder:text-gray-500 text-white rounded bg-theme px-3 py-2 focus:outline-none border-1 focus:ring-0 focus:border-b"
                              placeholder="Enter Aadhaar Number"
                              value={aadhaar}
                              onChange={aadhaarHandler}
                            />
                          </div>
                          {error && <div className="text-red-600 text-center mt-2">{error}</div>}
                        </div>
                      </div>
                    </div>
                    <div className="bg-theme px-4 py-3 pb-6 flex  justify-center sm:px-6">
                      <CustomButton
                        btnType="button"
                        title={"Verify "}
                        loading={isSubmitting}
                        isDisabled={isSubmitting}
                        containerStyles="mt-3 inline-flex w-full justify-center rounded-md bg-themeBlue px-3 py-2 text-sm font-semibold shadow-sm sm:mt-0 sm:w-auto"
                        handleClick={verifyAadhaar}
                      />

                      <button
                        type="submit"
                        className="mt-3 absolute top-5 bg-transparent right-5  justify-center rounded-full border-1 text-white p-2 text-sm font-semibold shadow-sm  sm:mt-0 sm:w-auto"
                        onClick={() => {
                          if (onClose) onClose();
                          setOpen(false);
                        }}
                        ref={cancelButtonRef}
                      >
                        <XMarkIcon className="h-4" />
                      </button>
                    </div>
                  </DialogPanel>
                </ TransitionChild>
              </div>
            </div>
          </Dialog>
        )}
      </Transition>
      {openOtpModal && (
        <OtpModal
          isOpen={openOtpModal}
          onClose={() => {
            setOpenOtpModal(false);
            setOtp("");
            setOtpError("");
          }}
          onSubmitVerify={handleOTPVerify}
          handleOTPChange={handleOTPChange}
          otp={otp}
          otpError={otpError}
          isSubmitting={isSubmitting}
          otpMsg={otpMsg}
        />
      )}
    </>
  );
}
